import { useEffect, useRef, useState } from 'react'

export interface Block {
  key: string
  node: React.ReactNode
  keepWithNext?: boolean
  /** Force this block to start at the top of a new page. */
  breakBefore?: boolean
}

/**
 * Measures every block (rendered hidden at true width) and packs them into pages
 * that never exceed `pageHeightPx`. Uses offsetHeight (NOT getBoundingClientRect,
 * which is affected by ancestor transforms) so it works even when the visible
 * tree is inside a zoom/pan transform. Retries until all heights are non-zero.
 */
function packByHeights(
  blocks: Block[],
  heights: Record<string, number>,
  pageHeightPx: number,
): Block[][] {
  // Accounts for the vertical spacing between stacked blocks (offsetHeight
  // excludes margins). Kept modest so pages pack tightly without stranding.
  const GAP = 16
  const result: Block[][] = []
  let current: Block[] = []
  let used = 0
  const flush = () => {
    if (current.length) result.push(current)
    current = []
    used = 0
  }
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]
    const h = heights[b.key] ?? 0
    const add = (current.length ? GAP : 0) + h
    // Forced page break: start this block on a fresh page.
    if (b.breakBefore && current.length > 0) flush()
    if (current.length > 0 && used + add > pageHeightPx) flush()
    if (b.keepWithNext && i + 1 < blocks.length) {
      const nextH = heights[blocks[i + 1].key] ?? 0
      const projected = (current.length ? used + GAP : 0) + h + GAP + nextH
      if (current.length > 0 && projected > pageHeightPx) flush()
    }
    current.push(b)
    used += current.length === 1 ? h : add
  }
  flush()
  return result
}

export function useMeasuredPages(
  blocks: Block[],
  pageHeightPx: number,
  variant = '',
) {
  const refs = useRef<Record<string, HTMLElement | null>>({})
  const [heights, setHeights] = useState<Record<string, number>>({})
  const sig = variant + '::' + blocks.map((b) => b.key).join('|')

  const setRef = (key: string) => (el: HTMLElement | null) => {
    refs.current[key] = el
  }

  useEffect(() => {
    let cancelled = false
    const attempt = (): boolean => {
      const h: Record<string, number> = {}
      for (const b of blocks) {
        const el = refs.current[b.key]
        if (!el) return false
        const ht = el.offsetHeight // transform-independent
        if (ht === 0) return false
        h[b.key] = ht
      }
      if (!cancelled) setHeights(h)
      return true
    }
    if (attempt()) return
    let tries = 0
    const iv = window.setInterval(() => {
      tries += 1
      if (attempt() || tries > 60) window.clearInterval(iv)
    }, 50)
    if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
      ;(document as any).fonts.ready.then(attempt)
    }
    return () => {
      cancelled = true
      window.clearInterval(iv)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, pageHeightPx])

  // Always return a paginated result. Before measurement, split with per-block
  // ESTIMATES so pages are separated immediately; refine once real heights land.
  const haveAll = blocks.length > 0 && blocks.every((b) => heights[b.key] > 0)
  const estimate = (b: Block): number => {
    if (b.key === 'masthead') return 300
    if (b.key === 'intro-band') return 340
    if (b.key === 'tail') return 300
    if (b.key.startsWith('role-')) return 300
    return 90
  }
  const effective: Record<string, number> = {}
  for (const b of blocks)
    effective[b.key] = haveAll ? heights[b.key] : estimate(b)
  const pages = packByHeights(blocks, effective, pageHeightPx)

  return { setRef, pages, measured: haveAll }
}
